import { useEffect, useState } from "react";

const PRODUCTION_URL = "https://link.linklitepro.com";
const PRODUCTION_HOSTS = new Set(["link.linklitepro.com", "www.link.linklitepro.com"]);

/**
 * Hard redirect ANY visitor on a non-production host to the live site.
 *
 * Dev bypass: pass ?dev=1 in the URL on EVERY page load. We deliberately do
 * NOT remember the bypass across page loads — sticky bypass via sessionStorage
 * was the root cause of customers (and the owner's phone) accidentally
 * staying in preview mode after visiting one dev link.
 *
 * Customers who land here for any reason see a giant "Open the live site"
 * button before the auto-redirect fires, so even on a flaky network they can
 * escape manually.
 */
export default function PreviewBanner() {
  const [phase, setPhase] = useState("checking"); // checking | redirecting | bypassed

  useEffect(() => {
    const host = window.location.hostname.toLowerCase();
    if (PRODUCTION_HOSTS.has(host)) {
      setPhase("bypassed");
      return;
    }

    // Per-load dev bypass only — must be present in this URL.
    // Clear any legacy sessionStorage flag so old bypasses can't linger.
    try { sessionStorage.removeItem("link_dev_bypass"); } catch { /* noop */ }
    try { localStorage.removeItem("link_dev_bypass"); } catch { /* noop */ }

    const params = new URLSearchParams(window.location.search);
    if (params.get("dev") === "1") {
      setPhase("bypassed");
      let meta = document.querySelector('meta[name="robots"]');
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("name", "robots");
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", "noindex, nofollow, noarchive");
      document.title = "[DEV] Link Ride Preview";
      return;
    }

    // Real visitor on preview → redirect to production
    setPhase("redirecting");
    const path = window.location.pathname + window.location.search + window.location.hash;
    const dest = PRODUCTION_URL + (path === "/" ? "" : path);
    // Give them a visible escape hatch button (next render). Auto-redirect
    // after 1.5s so on a flaky network they can still tap manually.
    setTimeout(() => { window.location.replace(dest); }, 1500);
  }, []);

  if (phase === "bypassed") {
    if (PRODUCTION_HOSTS.has(window.location.hostname.toLowerCase())) {
      // No badge on production
      return null;
    }
    // Dev preview — show clear top tag
    return (
      <div
        style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
          background: "#dc2626", color: "white",
          fontSize: 11, fontWeight: 700, textAlign: "center",
          padding: "4px 8px", letterSpacing: 0.5,
        }}
        data-testid="dev-bypass-badge"
      >
        DEV PREVIEW · public visitors are auto-redirected to {PRODUCTION_URL}
      </div>
    );
  }

  if (phase === "redirecting") {
    return (
      <div
        style={{
          position: "fixed", inset: 0, zIndex: 1000,
          background: "#050505", color: "white",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexDirection: "column", gap: 20, padding: 24,
          fontFamily: "system-ui",
        }}
        data-testid="redirect-screen"
      >
        <div style={{ fontSize: 32, fontWeight: 900, letterSpacing: 1 }}>Link Ride</div>
        <p style={{ opacity: 0.7, fontSize: 14, textAlign: "center", maxWidth: 320 }}>
          Taking you to the live site…
        </p>
        <a
          href={PRODUCTION_URL}
          style={{
            background: "#0047FF", color: "white", textDecoration: "none",
            padding: "14px 28px", borderRadius: 999,
            fontWeight: 800, fontSize: 15, letterSpacing: 0.3,
            boxShadow: "0 8px 24px -8px rgba(0,71,255,0.6)",
          }}
          data-testid="go-to-live-site"
        >
          Open the live site
        </a>
        <p style={{ opacity: 0.4, fontSize: 11, marginTop: 4 }}>
          {PRODUCTION_URL}
        </p>
      </div>
    );
  }

  return null;
}
